import { useState, useEffect, useCallback, useRef } from "react";

const PING_URL = "https://www.gstatic.com/generate_204";
const PING_TIMEOUT_MS = 3000;
const POLL_INTERVAL_MS = 3000; // poll every 3 s while offline

async function ping() {
  if (typeof navigator !== "undefined" && !navigator.onLine) return false;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), PING_TIMEOUT_MS);
    await fetch(PING_URL, { mode: "no-cors", cache: "no-store", signal: ctrl.signal });
    clearTimeout(t);
    return true;
  } catch {
    return false;
  }
}

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [isChecking, setIsChecking] = useState(false);

  const pendingRef       = useRef(false);
  const pollingRef       = useRef(null);  // setInterval id
  const delayRef         = useRef(null);  // setTimeout id (1 s delay before polling)
  const isPollingRef     = useRef(false); // guard: avoid duplicate intervals

  // ── Stop all polling timers ──────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (delayRef.current)   { clearTimeout(delayRef.current);   delayRef.current   = null; }
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    isPollingRef.current = false;
  }, []);

  // ── Start polling loop (called after the 1 s delay) ──────────────────────
  const startPolling = useCallback(() => {
    if (isPollingRef.current) return; // already running
    isPollingRef.current = true;
    pollingRef.current = setInterval(async () => {
      const ok = await ping();
      if (ok) {
        stopPolling();
        setIsOnline(true);
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  // ── Core verify: ping once; if it fails → wait 1 s → start polling ───────
  const verify = useCallback(async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;

    const ok = await ping();

    if (ok) {
      stopPolling();
      setIsOnline(true);
    } else {
      setIsOnline(false);
      // Only schedule the delay if we are not already polling
      if (!isPollingRef.current && !delayRef.current) {
        delayRef.current = setTimeout(() => {
          delayRef.current = null;
          startPolling();
        }, 1000);
      }
    }

    pendingRef.current = false;
  }, [startPolling, stopPolling]);

  // ── Cleanup on unmount ───────────────────────────────────────────────────
  useEffect(() => () => stopPolling(), [stopPolling]);

  // 1. Mount-time check
  useEffect(() => {
    verify();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 2. PRIMARY — SignalR WebSocket heartbeat signals (dashboard routes)
  useEffect(() => {
    const goOffline = () => {
      setIsOnline(false);
      // WS dropped → start polling after 1 s if not already
      if (!isPollingRef.current && !delayRef.current) {
        delayRef.current = setTimeout(() => {
          delayRef.current = null;
          startPolling();
        }, 1000);
      }
    };
    const goOnline = () => verify();
    window.addEventListener("app:ws:offline", goOffline);
    window.addEventListener("app:ws:online",  goOnline);
    return () => {
      window.removeEventListener("app:ws:offline", goOffline);
      window.removeEventListener("app:ws:online",  goOnline);
    };
  }, [verify, startPolling]);

  // 3. FALLBACK — browser online/offline events (auth pages + mobile)
  useEffect(() => {
    const goOnline  = () => { stopPolling(); setIsOnline(true); };
    const goOffline = () => {
      setIsOnline(false);
      if (!isPollingRef.current && !delayRef.current) {
        delayRef.current = setTimeout(() => {
          delayRef.current = null;
          startPolling();
        }, 1000);
      }
    };
    window.addEventListener("online",  goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online",  goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [startPolling, stopPolling]);

  // 4. Tab focus / visibility — one ping when user returns to the tab
  useEffect(() => {
    const onFocus   = () => verify();
    const onVisible = () => { if (document.visibilityState === "visible") verify(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [verify]);

  // 5. Axios / fetch network errors (in-flight API calls fail when WiFi drops)
  useEffect(() => {
    const onRejection = (event) => {
      const reason = event.reason;
      if (!reason || reason.response) return; // skip server errors (4xx/5xx)
      const msg  = reason.message || String(reason);
      const code = reason.code || "";
      const isNetworkFailure =
        msg === "Network Error"            ||
        msg === "Failed to fetch"          ||
        msg === "Load failed"              ||
        code === "ERR_NETWORK"             ||
        code === "ERR_INTERNET_DISCONNECTED";
      if (isNetworkFailure) verify();
    };
    window.addEventListener("unhandledrejection", onRejection);
    return () => window.removeEventListener("unhandledrejection", onRejection);
  }, [verify]);

  const retryConnection = useCallback(async () => {
    setIsChecking(true);
    const ok = await ping();
    if (ok) { stopPolling(); setIsOnline(true); }
    else     { setIsOnline(false); }
    setIsChecking(false);
    return ok;
  }, [stopPolling]);

  return { isOnline, isChecking, retryConnection };
}
